import * as React from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Check, Plus, Send, Trash2, X } from 'lucide-react'
import { useEstimate, useEstimateAction, useEstimateSignature, useSaveEstimate } from '@/lib/queries.js'
import { DepositOverrideCard, ShareLinkCard, SignatureCard } from './EstimateSigning.jsx'
import { money } from '@/lib/format.js'
import { cn } from '@/lib/utils.js'
import { Button } from '@/components/ui/button.jsx'
import { Card, CardBody, CardHeader, CardTitle, PageHeader } from '@/components/ui/card.jsx'
import { Field, Input, Label } from '@/components/ui/field.jsx'
import { Select, Textarea } from '@/components/ui/select.jsx'
import { Alert, Spinner } from '@/components/ui/feedback.jsx'
import { StatusBadge } from '@/components/ui/badge.jsx'
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog.jsx'
import { ERR, humanMessage, isWorkflow } from '@/lib/errors.js'

const BLANK_ITEM = { description: '', quantity: 1, unit_price: 0, taxable: true }

/**
 * Estimate editor (Phase 1b screen 6) — the only genuinely complex screen.
 *
 * ── THE RULE THAT SHAPES THIS ENTIRE FILE ───────────────────────────────────
 * Totals are computed SERVER-SIDE. `services/lineItems.js` recomputes every
 * line's total from quantity × unit_price and ignores any total the client
 * sends — Phase 1 proves this with an assertion that a caller-supplied
 * `total: 0.01` comes back as 2000.
 *
 * So this screen must never present browser arithmetic as the answer. It shows
 * two different things and labels them differently:
 *
 *   · a PREVIEW while editing, computed locally, explicitly marked as such —
 *     because a form where the numbers do not move until you save feels broken;
 *   · the SAVED totals, which come from the API response and are the truth.
 *
 * And when the two disagree after a save, it says so (see TotalsPanel) rather
 * than quietly showing whichever is handier. A silent disagreement here is
 * exactly the class of bug the §6 verification exists to catch, and hiding it
 * in the UI would defeat that test.
 *
 * ── DRAFT-ONLY EDITING ──────────────────────────────────────────────────────
 * A sent estimate answers 409 `not_draft` on PATCH. Rather than let someone
 * type into a form that cannot save, the inputs go read-only once sent, and the
 * screen explains why.
 */
export function EstimateEditor () {
  const { id, jobId } = useParams()
  const isNew = !id
  const navigate = useNavigate()

  // D26.2 / D27.7. A supplemental is an ordinary estimate carrying its
  // parent's id, and it must be reachable in a couple of taps from the job
  // while a customer is standing there — hence a query parameter on the
  // existing new-estimate route rather than a separate screen with its own
  // navigation to get lost in.
  const [searchParams] = useSearchParams()
  const supplementalOf = isNew ? searchParams.get('supplemental') : null

  const { data: estimate, isPending, error } = useEstimate(id)
  // Same query key as useEstimate, so this adds no request (see queries.js).
  const { data: signature } = useEstimateSignature(id)
  const save = useSaveEstimate()

  const [draft, setDraft] = React.useState(null)
  const [saveError, setSaveError] = React.useState(null)

  // Seed the form once from the server copy, then leave it alone — re-syncing
  // on every refetch would discard whatever the user is mid-way through typing.
  React.useEffect(() => {
    if (isNew && !draft) {
      setDraft({
        line_items: [{ ...BLANK_ITEM }],
        tax_rate: 0,
        discount_type: '',
        discount_value: 0,
        notes_client: '',
        // Phase 2 D22.2. '' means "use the workspace default", which is the
        // NULL the API stores — not a missing value.
        deposit_type: '',
        deposit_value: 0
      })
    } else if (estimate && !draft) {
      setDraft({
        line_items: estimate.line_items?.length ? estimate.line_items.map(i => ({ ...i })) : [{ ...BLANK_ITEM }],
        tax_rate: Number(estimate.tax_rate ?? 0),
        discount_type: estimate.discount_type ?? '',
        discount_value: Number(estimate.discount_value ?? 0),
        notes_client: estimate.notes_client ?? '',
        deposit_type: estimate.deposit_type ?? '',
        deposit_value: Number(estimate.deposit_value ?? 0)
      })
    }
  }, [estimate, isNew, draft])

  if (!isNew && isPending) {
    return <div className="flex justify-center py-16"><Spinner label="Loading estimate" /></div>
  }

  if (error) {
    return (
      <>
        <PageHeader back={<BackLink jobId={jobId} />} title="Could not load this estimate" />
        <Alert tone="danger">{humanMessage(error)}</Alert>
      </>
    )
  }

  if (!draft) return <div className="flex justify-center py-16"><Spinner /></div>

  const status = estimate?.status ?? 'draft'
  const editable = status === 'draft'
  const targetJobId = jobId ?? estimate?.job_id

  function setItem (index, patch) {
    setDraft(d => ({
      ...d,
      line_items: d.line_items.map((item, i) => (i === index ? { ...item, ...patch } : item))
    }))
  }

  async function handleSave () {
    setSaveError(null)
    const items = draft.line_items
      .filter(i => i.description.trim())
      .map(i => ({
        description: i.description.trim(),
        quantity: Number(i.quantity) || 0,
        unit_price: Number(i.unit_price) || 0,
        taxable: i.taxable !== false
        // NOTE: `total` is deliberately not sent. The server computes it, and
        // sending one would be ignored anyway — omitting it keeps that obvious.
      }))

    try {
      const { estimate: saved } = await save.mutateAsync({
        id,
        job_id: targetJobId,
        // Only ever sent on create. D32: the supplemental inherits its
        // parent's billing mode server-side, so nothing about it is decided
        // here.
        ...(supplementalOf ? { parent_estimate_id: supplementalOf } : {}),
        line_items: items,
        tax_rate: Number(draft.tax_rate) || 0,
        discount_type: draft.discount_type || null,
        discount_value: draft.discount_type ? Number(draft.discount_value) || 0 : 0,
        notes_client: draft.notes_client.trim() || null,
        // D22.2: null deposit_type means "use the tenant default". The frozen
        // deposit_amount is never sent — the acceptance transition writes it.
        deposit_type: draft.deposit_type || null,
        deposit_value: draft.deposit_type ? Number(draft.deposit_value) || 0 : 0
      })
      if (isNew) navigate(`/estimates/${saved.id}`, { replace: true })
    } catch (err) {
      setSaveError(err)
    }
  }

  const preview = previewTotals(draft)

  return (
    <>
      <PageHeader
        back={<BackLink jobId={targetJobId} />}
        title={isNew ? (supplementalOf ? 'Extra work' : 'New estimate') : estimate.estimate_number}
        description={
          <span className="flex items-center gap-2">
            <StatusBadge status={status} />
            {!editable && <span className="text-sm">Sent estimates cannot be edited.</span>}
          </span>
        }
        actions={<EstimateActions estimate={estimate} editable={editable} onSave={handleSave} saving={save.isPending} isNew={isNew} draft={draft} />}
      />

      {supplementalOf && (
        <Alert tone="info" className="mb-4" data-testid="supplemental-banner">
          This is extra work added to a job that has already been signed for. It
          gets its own signature, and it is billed the same way as the original.
          The signed estimate it follows is not changed.
        </Alert>
      )}

      {saveError && (
        <Alert tone={isWorkflow(saveError) ? 'warn' : 'danger'} className="mb-4">
          {saveError.slug === ERR.INVALID_LINE_ITEMS
            ? `Check the line items — ${saveError.message}`
            : humanMessage(saveError)}
        </Alert>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Card>
            <CardHeader><CardTitle>Line items</CardTitle></CardHeader>
            <CardBody className="space-y-3">
              {draft.line_items.map((item, index) => (
                <LineItemRow
                  key={index}
                  item={item}
                  index={index}
                  editable={editable}
                  onChange={patch => setItem(index, patch)}
                  onRemove={() => setDraft(d => ({
                    ...d,
                    line_items: d.line_items.length === 1
                      ? [{ ...BLANK_ITEM }]
                      : d.line_items.filter((_, i) => i !== index)
                  }))}
                />
              ))}

              {editable && (
                <Button
                  variant="secondary"
                  onClick={() => setDraft(d => ({ ...d, line_items: [...d.line_items, { ...BLANK_ITEM }] }))}
                  data-testid="add-line-item"
                >
                  <Plus /> Add line
                </Button>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader><CardTitle>Notes for the client</CardTitle></CardHeader>
            <CardBody>
              <Label htmlFor="est-notes" className="sr-only">Notes for the client</Label>
              <Textarea
                id="est-notes"
                value={draft.notes_client}
                onChange={e => setDraft(d => ({ ...d, notes_client: e.target.value }))}
                disabled={!editable}
                placeholder="Anything the client should read alongside the price."
              />
            </CardBody>
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader><CardTitle>Adjustments</CardTitle></CardHeader>
            <CardBody className="space-y-4">
              <Field
                id="tax-rate" label="Tax rate" type="number" inputMode="decimal"
                min="0" step="0.01" value={draft.tax_rate}
                onChange={e => setDraft(d => ({ ...d, tax_rate: e.target.value }))}
                disabled={!editable} hint="Percent, e.g. 8.25"
              />

              <div className="space-y-1.5">
                <Label htmlFor="discount-type">Discount</Label>
                <Select
                  id="discount-type" value={draft.discount_type} disabled={!editable}
                  onChange={e => setDraft(d => ({ ...d, discount_type: e.target.value }))}
                >
                  <option value="">None</option>
                  <option value="percentage">Percentage</option>
                  <option value="flat">Flat amount</option>
                </Select>
              </div>

              {draft.discount_type && (
                <Field
                  id="discount-value"
                  label={draft.discount_type === 'percentage' ? 'Discount %' : 'Discount amount'}
                  type="number" inputMode="decimal" min="0" step="0.01"
                  value={draft.discount_value}
                  onChange={e => setDraft(d => ({ ...d, discount_value: e.target.value }))}
                  disabled={!editable}
                />
              )}
            </CardBody>
          </Card>

          <TotalsPanel estimate={estimate} preview={preview} isNew={isNew} />

          {/* Phase 2 (item 16a). Ordered the way the work happens: decide the
              deposit, get it signed, then read the signature back. */}
          <DepositOverrideCard
            draft={draft}
            setDraft={setDraft}
            // While it is editable the DRAFT is what will be saved, so the
            // deposit figure has to track what the user is typing. Once it is
            // sent, the server's own total is the authority.
            grandTotal={editable ? preview.total : (estimate?.total ?? preview.total)}
            editable={editable}
          />

          {!isNew && <ShareLinkCard estimate={estimate} signed={Boolean(signature)} />}
          {!isNew && <SignatureCard estimateId={id} />}
        </div>
      </div>
    </>
  )
}

function LineItemRow ({ item, index, editable, onChange, onRemove }) {
  // Preview only. The server recomputes this and its answer wins.
  const lineTotal = (Number(item.quantity) || 0) * (Number(item.unit_price) || 0)

  return (
    <div className="rounded-md border border-line p-3" data-testid="line-item">
      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor={`li-desc-${index}`} className="sr-only">Description</Label>
          <Input
            id={`li-desc-${index}`}
            value={item.description}
            onChange={e => onChange({ description: e.target.value })}
            placeholder="What is it?"
            disabled={!editable}
            data-testid="line-item-description"
          />
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="space-y-1.5">
            <Label htmlFor={`li-qty-${index}`} className="text-xs">Qty</Label>
            <Input
              id={`li-qty-${index}`} type="number" inputMode="decimal" min="0" step="any"
              value={item.quantity} onChange={e => onChange({ quantity: e.target.value })}
              disabled={!editable} className="tabular"
              data-testid="line-item-quantity"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`li-price-${index}`} className="text-xs">Unit price</Label>
            <Input
              id={`li-price-${index}`} type="number" inputMode="decimal" min="0" step="0.01"
              value={item.unit_price} onChange={e => onChange({ unit_price: e.target.value })}
              disabled={!editable} className="tabular"
              data-testid="line-item-price"
            />
          </div>
          <div className="flex items-end">
            <label className="flex min-h-tap items-center gap-2 text-sm text-ink-muted">
              <input
                type="checkbox" checked={item.taxable !== false} disabled={!editable}
                onChange={e => onChange({ taxable: e.target.checked })}
                className="size-4 rounded border-line-strong accent-[hsl(var(--brand))]"
              />
              Taxable
            </label>
          </div>
          <div className="flex items-end justify-between gap-2">
            <span className="tabular pb-2 text-sm font-medium text-ink">{money(lineTotal)}</span>
            {editable && (
              <Button variant="ghost" size="icon-sm" onClick={onRemove} aria-label={`Remove line ${index + 1}`}>
                <Trash2 />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Totals.
 *
 * Shows the SAVED figures from the API when they exist. The local preview is
 * only shown for an unsaved estimate, and it is labelled.
 *
 * The mismatch banner is the important part. If the browser's arithmetic and
 * the server's disagree after a save, that is a real defect — a rounding rule
 * drifting apart, or a discount applied in a different order — and the product
 * should say so rather than pick a winner. Phase 1's own tests assert the
 * server's answer; this makes a divergence visible to a human too.
 */
function TotalsPanel ({ estimate, preview, isNew }) {
  const saved = estimate
    ? {
        subtotal: Number(estimate.subtotal),
        discount: Number(estimate.discount_amount ?? 0),
        tax: Number(estimate.tax_amount ?? 0),
        total: Number(estimate.total)
      }
    : null

  // Half a cent of tolerance: a genuine disagreement is never this small, and
  // float display noise should not raise an alarm.
  const mismatch = saved && Math.abs(saved.total - preview.total) > 0.005

  return (
    <Card>
      <CardHeader>
        <CardTitle>{saved ? 'Totals' : 'Preview'}</CardTitle>
      </CardHeader>
      <CardBody className="space-y-2">
        {!saved && (
          <p className="mb-1 text-xs text-ink-subtle">
            Estimated in your browser. Saving computes the real figures.
          </p>
        )}

        <Row label="Subtotal" value={saved ? saved.subtotal : preview.subtotal} />
        {(saved ? saved.discount : preview.discount) > 0 && (
          <Row label="Discount" value={-(saved ? saved.discount : preview.discount)} />
        )}
        <Row label="Tax" value={saved ? saved.tax : preview.tax} />
        <div className="border-t border-line pt-2">
          <Row
            label="Total" bold
            value={saved ? saved.total : preview.total}
            testId="estimate-total"
          />
        </div>

        {mismatch && (
          <Alert tone="warn" title="Totals do not match" className="mt-3">
            The saved total ({money(saved.total)}) differs from what this screen
            calculated ({money(preview.total)}). The saved figure is the real one
            — please report this.
          </Alert>
        )}

        {isNew && (
          <p className="pt-1 text-xs text-ink-subtle">
            Nothing is saved until you press Save.
          </p>
        )}
      </CardBody>
    </Card>
  )
}

function Row ({ label, value, bold, testId }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className={cn('text-sm', bold ? 'font-medium text-ink' : 'text-ink-muted')}>{label}</span>
      <span
        className={cn('tabular text-sm', bold ? 'font-semibold text-ink' : 'text-ink')}
        data-testid={testId}
      >
        {money(value)}
      </span>
    </div>
  )
}

function EstimateActions ({ estimate, editable, onSave, saving, isNew, draft }) {
  const send = useEstimateAction('send')
  const accept = useEstimateAction('accept')
  const decline = useEstimateAction('decline')
  const [accepting, setAccepting] = React.useState(false)
  const [error, setError] = React.useState(null)

  const hasContent = draft.line_items.some(i => i.description.trim())

  if (editable) {
    return (
      <>
        <Button onClick={onSave} loading={saving} disabled={!hasContent} data-testid="save-estimate">
          Save
        </Button>
        {!isNew && (
          <Button
            variant="secondary"
            loading={send.isPending}
            onClick={() => send.mutateAsync({ id: estimate.id }).catch(setError)}
            data-testid="send-estimate"
          >
            <Send /> Send
          </Button>
        )}
        {error && <Alert tone="danger">{humanMessage(error)}</Alert>}
      </>
    )
  }

  if (estimate?.status === 'sent' || estimate?.status === 'viewed') {
    return (
      <>
        <Button variant="secondary" onClick={() => decline.mutateAsync({ id: estimate.id }).catch(setError)} loading={decline.isPending}>
          <X /> Decline
        </Button>
        <Button onClick={() => setAccepting(true)} data-testid="accept-estimate">
          <Check /> Accept
        </Button>
        <AcceptDialog
          open={accepting} onOpenChange={setAccepting}
          onAccept={name => accept.mutateAsync({ id: estimate.id, acceptedByName: name })}
          pending={accept.isPending}
        />
      </>
    )
  }

  return null
}

/**
 * Click-accept (Phase 1 D6 / master-roadmap).
 *
 * This records a name and a timestamp. It is explicitly NOT a signature — real
 * e-signature with a signed PDF and an audit trail is Phase 2, alongside
 * deposit collection, because signing and paying a deposit are one customer
 * motion. Saying so here keeps anyone from mistaking this for the finished
 * thing.
 */
function AcceptDialog ({ open, onOpenChange, onAccept, pending }) {
  const [name, setName] = React.useState('')
  const [error, setError] = React.useState(null)

  React.useEffect(() => { if (open) { setName(''); setError(null) } }, [open])

  async function submit (event) {
    event.preventDefault()
    setError(null)
    try {
      await onAccept(name.trim())
      onOpenChange(false)
    } catch (err) { setError(err) }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title="Accept this estimate"
        description="Records who accepted and when. A signed document comes with payments."
      >
        <form id="accept-form" onSubmit={submit} className="space-y-4" noValidate>
          {error && <Alert tone="danger">{humanMessage(error)}</Alert>}
          <Field
            id="accepted-by" label="Who accepted it?" value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Their name" required autoComplete="off"
            data-testid="accepted-by-name"
          />
        </form>
        <DialogFooter className="-mx-5 -mb-4 mt-4">
          <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="submit" form="accept-form" loading={pending} disabled={!name.trim()} data-testid="confirm-accept">
            Accept
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Local preview arithmetic.
 *
 * Mirrors services/lineItems.js — discount reduces the taxable base
 * proportionally, tax applies only to taxable lines. Kept deliberately simple:
 * it exists to make the form feel alive, not to be authoritative, and the
 * mismatch banner above is what handles it drifting from the server.
 */
function previewTotals (draft) {
  let subtotal = 0
  let taxableBase = 0

  for (const item of draft.line_items) {
    const line = (Number(item.quantity) || 0) * (Number(item.unit_price) || 0)
    subtotal += line
    if (item.taxable !== false) taxableBase += line
  }

  let discount = 0
  if (draft.discount_type === 'percentage') {
    discount = subtotal * ((Number(draft.discount_value) || 0) / 100)
  } else if (draft.discount_type === 'flat') {
    discount = Math.min(Number(draft.discount_value) || 0, subtotal)
  }

  if (discount > 0 && subtotal > 0) {
    taxableBase = taxableBase * (1 - discount / subtotal)
  }

  const tax = taxableBase * ((Number(draft.tax_rate) || 0) / 100)
  const round = n => Math.round(n * 100) / 100

  return {
    subtotal: round(subtotal),
    discount: round(discount),
    tax: round(tax),
    total: round(subtotal - discount + tax)
  }
}

function BackLink ({ jobId }) {
  return (
    <Link
      to={jobId ? `/jobs/${jobId}` : '/'}
      className="mb-2 inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink"
    >
      <ArrowLeft className="size-4" aria-hidden="true" /> {jobId ? 'Job' : 'Pipeline'}
    </Link>
  )
}
